/**
 * Chat bubble primitives — ported from the shadcn/ui registry
 * (`radix-nova/bubble`, fetched 2026-09-16) and retokenized to Meridian.
 * The file is ours from here on: it is not synchronized with the registry
 * (spec §8.0.1, D15).
 *
 * Replacements against the registry source: `cva` → an inline variant
 * dictionary (class-variance-authority is not a ui-worker dependency);
 * `Slot` from `@radix-ui/react-slot`; `cn` from `@/lib/utils`. Only the
 * `default`, `ghost` and `outline` variants survive — `secondary`, `muted`,
 * `tinted` and `destructive` were built on `--primary`/`--secondary`
 * variables Meridian does not have, and every `dark:` variant is gone: the
 * theme switches through Meridian variables, not `prefers-color-scheme`.
 * `BubbleReactions` is not ported — nothing in the assistant renders it.
 *
 * Tokens: shadcn's primary + its foreground → `bg-ai`/`text-bg-void`,
 * `bg-primary/80` → `bg-ai-hover`, `bg-background` → `bg-bg-elevated`,
 * `hover:bg-muted` → `hover:bg-bg-highlight`, `text-foreground` → `text-fg`.
 */

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/lib/utils';

function BubbleGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="bubble-group"
      className={cn('flex min-w-0 flex-col gap-2', className)}
      {...props}
    />
  );
}

export type BubbleVariant = 'default' | 'ghost' | 'outline' | 'subtle';

const BUBBLE_BASE =
  'group/bubble relative flex w-fit max-w-[80%] min-w-0 flex-col gap-1 group-data-[align=end]/message:self-end data-[align=end]:self-end data-[variant=ghost]:max-w-full';

const BUBBLE_VARIANTS: Record<BubbleVariant, string> = {
  default:
    '*:data-[slot=bubble-content]:bg-ai *:data-[slot=bubble-content]:text-bg-void [&>[data-slot=bubble-content]:is(button,a):hover]:bg-ai-hover',
  outline:
    '*:data-[slot=bubble-content]:border-border *:data-[slot=bubble-content]:bg-bg-elevated [&>[data-slot=bubble-content]:is(button,a):hover]:bg-bg-highlight [&>[data-slot=bubble-content]:is(button,a):hover]:text-fg',
  // Ours, not the registry's: the user's own question (§8.0.1).
  subtle:
    '*:data-[slot=bubble-content]:bg-bg-subtle *:data-[slot=bubble-content]:text-fg *:data-[slot=bubble-content]:rounded-card',
  ghost:
    'border-none *:data-[slot=bubble-content]:rounded-none *:data-[slot=bubble-content]:bg-transparent *:data-[slot=bubble-content]:p-0 [&>[data-slot=bubble-content]:is(button,a):hover]:bg-bg-highlight [&>[data-slot=bubble-content]:is(button,a):hover]:text-fg',
};

function Bubble({
  variant = 'default',
  align = 'start',
  className,
  ...props
}: React.ComponentProps<'div'> & {
  variant?: BubbleVariant;
  align?: 'start' | 'end';
}) {
  return (
    <div
      data-slot="bubble"
      data-variant={variant}
      data-align={align}
      className={cn(BUBBLE_BASE, BUBBLE_VARIANTS[variant], className)}
      {...props}
    />
  );
}

function BubbleContent({
  asChild = false,
  className,
  ...props
}: React.ComponentProps<'div'> & {
  asChild?: boolean;
}) {
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      data-slot="bubble-content"
      className={cn(
        'w-fit max-w-full min-w-0 overflow-hidden rounded-xl border border-transparent px-3 py-2 text-sm leading-relaxed wrap-break-word group-data-[align=end]/bubble:self-end [button]:text-left [button,a]:transition-colors [button,a]:outline-none [button,a]:focus-visible:border-ring [button,a]:focus-visible:ring-2 [button,a]:focus-visible:ring-ring',
        className
      )}
      {...props}
    />
  );
}

export { BubbleGroup, Bubble, BubbleContent };
